import React from "react";
import type { Medication } from "../../types/medication";
import logoMimap from "../../assets/Logo MIMAP Horizontal second--02.png";
import { CLINIC_INFO } from "../../utils/preEmploymentUtils";

interface PrescriptionContentProps {
  medications: Medication[];
  patientInfo: {
    nom: string;
    prenom: string;
    dateNaissance: string;
    adresse?: string;
  };
  doctorInfo: {
    firstName: string;
    lastName: string;
    title: string;
    specialty?: string; // ✅ Ajouter la spécialité du médecin
  };
  consultationDate: string;
  orderNumber?: string;
  instructions?: string;
}

const PrescriptionContent: React.FC<PrescriptionContentProps> = ({
  medications,
  patientInfo,
  doctorInfo,
  consultationDate,
  orderNumber,
}) => {
  const formatOrderNumber = (orderNum: string | undefined) => {
    if (!orderNum) return "En attente";

    // Format attendu: ORD-YYYY-SEQ-PATIENT-DOC
    // Exemple: ORD-2025-0001-0001-001
    const parts = orderNum.split("-");
    if (parts.length !== 5) return orderNum;

    const [, year, seq, patient, doctor] = parts;

    return `ORD-${year}-${seq}-${patient}-${doctor}`;
  };
  const containerStyle: React.CSSProperties = {
    width: "128mm",
    maxWidth: "none",
    height: "auto",
    padding: "20px",
    margin: "0 auto",
    boxSizing: "border-box",
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica, Arial, sans-serif",
    fontSize: "10px",
    lineHeight: "1.2",
    color: "#1f2937",
    position: "relative",
  };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
    paddingBottom: "8px",
    borderBottom: "2px solid #113285",
  };

  const logoStyle: React.CSSProperties = {
    width: "150px",
    height: "50px",
    objectFit: "contain",
  };

  const headerTextStyle: React.CSSProperties = {
    textAlign: "right",
    flex: "1",
    paddingLeft: "15px",
  };

  const documentTitleStyle: React.CSSProperties = {
    textAlign: "center",
    marginBottom: "10px",
    padding: "6px",
    backgroundColor: "#f8f9ff",
    borderRadius: "4px",
  };

  const documentTitleTextStyle: React.CSSProperties = {
    fontSize: "12px",
    fontWeight: "bold",
    color: "#113285",
    marginBottom: "1px",
  };

  const documentNumberStyle: React.CSSProperties = {
    fontSize: "8px",
    color: "#6b7280",
    fontWeight: "normal",
  };

  const documentDateStyle: React.CSSProperties = {
    fontSize: "8px",
    color: "#6b7280",
    fontWeight: "normal",
  };

  const doctorInfoStyle: React.CSSProperties = {
    marginTop: "15px",
    textAlign: "center",
    fontSize: "10px",
    color: "#1f2937",
  };

  const footerStyle: React.CSSProperties = {
    marginTop: "30px",
    paddingTop: "10px",
    borderTop: "1px solid #e5e7eb",
    fontSize: "8px",
    color: "#6b7280",
    textAlign: "center",
  };

  const footerTextStyle: React.CSSProperties = {
    fontSize: "7px",
    color: "#6b7280",
    marginBottom: "1px",
  };

  const patientSectionStyle: React.CSSProperties = {
    backgroundColor: "#fafbff",
    padding: "8px",
    marginBottom: "10px",
    borderRadius: "4px",
    borderLeft: "3px solid #113285",
  };

  const patientTitleStyle: React.CSSProperties = {
    fontSize: "10px",
    fontWeight: "bold",
    color: "#113285",
    marginBottom: "6px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  const patientGridStyle: React.CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
  };

  const patientItemStyle: React.CSSProperties = {
    width: "48%",
    marginBottom: "3px",
  };

  const patientLabelStyle: React.CSSProperties = {
    fontSize: "7px",
    color: "#6b7280",
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
  };

  const patientValueStyle: React.CSSProperties = {
    fontSize: "9px",
    color: "#111827",
    fontWeight: "normal",
    marginTop: "0.5px",
  };

  const medicationsSectionStyle: React.CSSProperties = {
    marginBottom: "15px",
  };

  const medicationsTitleStyle: React.CSSProperties = {
    fontSize: "10px",
    fontWeight: "bold",
    color: "#113285",
    marginBottom: "8px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "1px solid #e5e7eb",
    paddingBottom: "3px",
  };

  const medicationsListStyle: React.CSSProperties = {
    paddingLeft: "0",
  };

  const medicationItemStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: "5px",
    paddingTop: "3px",
    paddingBottom: "3px",
  };

  const medicationNumberStyle: React.CSSProperties = {
    width: "18px",
    fontSize: "8px",
    fontWeight: "bold",
    color: "#113285",
    textAlign: "right",
    marginRight: "8px",
  };

  const medicationContentStyle: React.CSSProperties = {
    flex: "1",
    display: "flex",
    flexDirection: "column",
    flexWrap: "wrap",
  };

  const medicationNameStyle: React.CSSProperties = {
    fontSize: "9px",
    color: "#111827",
    fontWeight: "normal",
    marginBottom: "2px",
    lineHeight: "1.3",
  };

  const medicationDetailsStyle: React.CSSProperties = {
    fontSize: "8px",
    color: "#6b7280",
    fontStyle: "italic",
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="prescription-content" style={containerStyle}>
      <style>
        {`
          @media print {
            .prescription-content {
              width: 128mm !important;
              padding: 20px !important;
              margin: 0 !important;
              box-shadow: none !important;
            }
            body { margin: 0; padding: 0; }
            .print-hidden { display: none !important; }
            @page { size: A5; margin: 8mm; }
          }
        `}
      </style>

      <div style={headerStyle}>
        <img src={logoMimap} alt="Logo MIMAP" style={logoStyle} />
        <div style={headerTextStyle}></div>
      </div>

      <div style={documentTitleStyle}>
        <div style={documentTitleTextStyle}>ORDONNANCE MÉDICALE</div>
        <div style={documentNumberStyle}>{formatOrderNumber(orderNumber)}</div>
        <div style={documentDateStyle}>
          Émise le {formatDate(consultationDate)}
        </div>
      </div>

      <div style={patientSectionStyle}>
        <div style={patientTitleStyle}>Informations Patient</div>
        <div style={patientGridStyle}>
          <div style={patientItemStyle}>
            <div style={patientLabelStyle}>Nom complet</div>
            <div style={patientValueStyle}>
              {patientInfo.nom} {patientInfo.prenom}
            </div>
          </div>
          <div style={patientItemStyle}>
            <div style={patientLabelStyle}>Date de naissance</div>
            <div style={patientValueStyle}>
              {formatDate(patientInfo.dateNaissance)}
            </div>
          </div>
        </div>
      </div>

      <div style={medicationsSectionStyle}>
        <div style={medicationsTitleStyle}>Médicaments prescrits</div>
        <div style={medicationsListStyle}>
          {medications.length > 0 ? (
            medications.map((med, index) => {
              const parts: string[] = [];
              parts.push(`Qté: ${med.quantity} ${med.quantityUnit}`);
              parts.push(
                `Posologie: ${med.dose} ${med.doseUnit}, ${med.frequency} ${med.interval}`
              );
              parts.push(`Durée: ${med.duration} ${med.durationUnit}`);
              if (med.startDate)
                parts.push(
                  `Du: ${med.startDate}${med.endDate ? ` au ${med.endDate}` : ""}`
                );
              if (med.advice) parts.push(`💡 ${med.advice}`);
              return (
                <div key={`${med.id || index}`} style={medicationItemStyle}>
                  <div style={medicationNumberStyle}>{index + 1}.</div>
                  <div style={medicationContentStyle}>
                    <div style={medicationNameStyle}>{med.name}</div>
                    <div style={medicationDetailsStyle}>{parts.join(" • ")}</div>
                  </div>
                </div>
              );
            })
          ) : (
            <div style={{ fontSize: "9px", color: "#111827", padding: "10px 0" }}>
              Aucun médicament spécifié.
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: "40px", display: "flex", justifyContent: "flex-end" }}>
        <div style={{ width: "50%", display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
          <div style={{ marginBottom: "40px", textAlign: "right" }}>
            <div style={{ fontSize: "9px", color: "#4a5568" }}>Signature et cachet du médecin</div>
          </div>

          <div style={{ ...doctorInfoStyle, textAlign: "right", marginTop: "0" }}>
            <div style={{ fontWeight: "bold", fontSize: "10px", color: "#113285" }}>
              {doctorInfo.title} {doctorInfo.firstName} {doctorInfo.lastName}
            </div>
            <div style={{ fontSize: "9px", color: "#4b5563" }}>
              {doctorInfo.specialty || "Médecin Généraliste"}
            </div>
            <div style={{ marginTop: "5px", fontSize: "8px", color: "#6b7280" }}>
              Fait le{" "}
              {new Date(consultationDate).toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </div>
          </div>
        </div>
      </div>

      <div style={footerStyle}>
        <div style={footerTextStyle}>{CLINIC_INFO.address}</div>
        <div style={footerTextStyle}>
          Tél: {CLINIC_INFO.phone} • Email: {CLINIC_INFO.email}
        </div>
      </div>
    </div>
  );
};

export default PrescriptionContent;
